/**
 * Boxy voxel AABB player + pure third-person orbit camera (no FP).
 * Phase 2.1: stacked rectangular voxels instead of capsule/pill silhouette.
 */

import * as THREE from "three";
import { aabbFromCenter, moveAndCollide, type Aabb } from "./aabb";
import type { InputManager } from "./input";
import type { World } from "./world";

export interface PlayerConfig {
  halfWidth: number;
  height: number;
  eyeHeight: number;
  moveSpeed: number;
  sprintMult: number;
  jumpSpeed: number;
  gravity: number;
  /** Camera distance behind player. */
  camDistance: number;
  camHeight: number;
  camMinPitch: number;
  camMaxPitch: number;
}

/**
 * Phase 1a: VOXEL_SCALE=20 — body stays ~human in "meters" of feel, but
 * 1 block = 1/20 prior body-relative size (player is ~32 blocks tall).
 * Linear motion/cam/gravity scaled with the same factor so dt physics match.
 * Trees 40–80 / rocks 20–60 / rolling hills read large next to this figure.
 */
export const VOXEL_SCALE = 20;

/** Auto-step ledges up to this many voxels; taller = wall (playtest 1a). */
export const MAX_AUTO_STEP_VOXELS = 3;

export const DEFAULT_PLAYER: PlayerConfig = {
  halfWidth: 0.35 * VOXEL_SCALE,
  height: 1.7 * VOXEL_SCALE,
  eyeHeight: 1.5 * VOXEL_SCALE,
  moveSpeed: 5.2 * VOXEL_SCALE,
  /** PLAYTEST 1 was 1.45; +~10% sprint. */
  sprintMult: 1.595,
  jumpSpeed: 7.5 * VOXEL_SCALE,
  gravity: 22 * VOXEL_SCALE,
  camDistance: 4.2 * VOXEL_SCALE,
  camHeight: 1.35 * VOXEL_SCALE,
  camMinPitch: -0.9,
  camMaxPitch: 0.55,
};

export class Player {
  readonly cfg: PlayerConfig;
  /** Feet position (bottom of AABB). */
  x = 0;
  y = 16;
  z = 0;
  vx = 0;
  vy = 0;
  vz = 0;
  grounded = false;

  /** Yaw around Y (look direction). Pitch for camera only. */
  yaw = 0;
  pitch = 0.25;

  readonly mesh: THREE.Group;
  private readonly world: World;

  // Reused temps — avoid per-frame Vector3 churn (FPS).
  private readonly _target = new THREE.Vector3();
  private readonly _camPos = new THREE.Vector3();
  private readonly _dir = new THREE.Vector3();

  constructor(world: World, cfg: PlayerConfig = DEFAULT_PLAYER) {
    this.world = world;
    this.cfg = cfg;

    this.mesh = new THREE.Group();
    this.mesh.name = "player";
    this.buildVoxelMesh(cfg);
  }

  /**
   * Stacked rectangular voxels — legs, torso, arms, head.
   * Collision AABB stays a single box; this is visual only.
   */
  private buildVoxelMesh(cfg: PlayerConfig): void {
    const S = VOXEL_SCALE;
    const skin = new THREE.MeshLambertMaterial({ color: 0xffe0c8 });
    const shirt = new THREE.MeshLambertMaterial({ color: 0x6ec6ff });
    const pants = new THREE.MeshLambertMaterial({ color: 0x4a6fa5 });
    const hair = new THREE.MeshLambertMaterial({ color: 0x5c3d2e });

    const addBox = (
      mat: THREE.Material,
      w: number,
      h: number,
      d: number,
      x: number,
      yBottom: number,
      z: number,
    ) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
      m.position.set(x, yBottom + h * 0.5, z);
      m.castShadow = false;
      this.mesh.add(m);
    };

    // Dimensions in world units (S = one “meter” of feel ≈ 20 voxels)
    const legW = 0.28 * S;
    const legH = 0.55 * S;
    const legGap = 0.08 * S;
    const torsoW = 0.7 * S;
    const torsoH = 0.65 * S;
    const torsoD = 0.38 * S;
    const armW = 0.22 * S;
    const armH = 0.55 * S;
    const headS = 0.42 * S;
    const hairH = 0.14 * S;

    // Legs
    addBox(pants, legW, legH, legW, -(legW * 0.5 + legGap * 0.5), 0, 0);
    addBox(pants, legW, legH, legW, legW * 0.5 + legGap * 0.5, 0, 0);
    // Torso
    addBox(shirt, torsoW, torsoH, torsoD, 0, legH, 0);
    // Arms
    const armY = legH + torsoH - armH;
    addBox(shirt, armW, armH, armW, -(torsoW * 0.5 + armW * 0.55), armY, 0);
    addBox(shirt, armW, armH, armW, torsoW * 0.5 + armW * 0.55, armY, 0);
    // Head + hair cap
    const headY = legH + torsoH;
    addBox(skin, headS, headS, headS, 0, headY, 0);
    addBox(hair, headS * 1.05, hairH, headS * 1.05, 0, headY + headS, 0);

    // Soft sanity: total visual height near cfg.height
    void cfg;
  }

  get aabb(): Aabb {
    return aabbFromCenter(
      this.x,
      this.y,
      this.z,
      this.cfg.halfWidth,
      this.cfg.height,
    );
  }

  /** Spawn on surface at xz. */
  spawnAt(wx: number, wz: number): void {
    // Stationary spawn — no heading bias until the player actually moves.
    this.world.updateAround(wx, wz, 0, 0);
    const sy = this.world.findSurfaceY(wx, wz);
    this.x = wx + 0.5;
    this.y = sy + 1;
    this.z = wz + 0.5;
    this.vx = this.vy = this.vz = 0;
    this.syncMesh();
  }

  update(dt: number, input: InputManager): void {
    // Look FIRST every frame — pure orbit; works while WASD held.
    // Never gates on grounded/velocity (playtest: look-while-moving).
    const look = input.consumeLook();
    if (look.dx !== 0 || look.dy !== 0) {
      this.yaw -= look.dx;
      this.pitch -= look.dy;
      this.pitch = Math.max(
        this.cfg.camMinPitch,
        Math.min(this.cfg.camMaxPitch, this.pitch),
      );
    }

    const move = input.moveVector();
    const speed =
      this.cfg.moveSpeed * (input.actions.sprint ? this.cfg.sprintMult : 1);

    // Camera-relative XZ (yaw drives facing while moving)
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    // move.z is forward(-)/back(+) in view; convert
    const wishX = move.x * cos + move.z * sin;
    const wishZ = -move.x * sin + move.z * cos;

    this.vx = wishX * speed;
    this.vz = wishZ * speed;

    if (this.grounded && input.actions.jump) {
      this.vy = this.cfg.jumpSpeed;
      this.grounded = false;
    }

    this.vy -= this.cfg.gravity * dt;

    // Substep for fast falls; auto-step ≤ MAX_AUTO_STEP_VOXELS when grounded / walking
    const steps = Math.max(1, Math.ceil(dt / (1 / 60)));
    const sdt = dt / steps;
    const maxStep =
      this.grounded || this.vy <= 0 ? MAX_AUTO_STEP_VOXELS : 0;
    for (let i = 0; i < steps; i++) {
      const box = this.aabb;
      const res = moveAndCollide(
        box,
        this.vx * sdt,
        this.vy * sdt,
        this.vz * sdt,
        (x, y, z) => this.world.isSolidWorld(x, y, z),
        { maxStepHeight: maxStep },
      );
      this.x += res.dx;
      this.y += res.dy;
      this.z += res.dz;
      this.grounded = res.grounded;
      // Clear vertical velocity when we hit floor/ceiling; keep small step-up lift
      if (this.vy < 0 && res.grounded) this.vy = 0;
      if (this.vy > 0 && res.dy < this.vy * sdt - 1e-4) this.vy = 0;
      if (res.dx === 0) this.vx = 0;
      if (res.dz === 0) this.vz = 0;
    }

    // Prevent void fall
    if (this.y < -40) {
      this.spawnAt(0, 0);
    }

    this.syncMesh();
  }

  private syncMesh(): void {
    this.mesh.position.set(this.x, this.y, this.z);
    this.mesh.rotation.y = this.yaw;
  }

  /**
   * Pure third-person follow. Camera looks at upper torso; never parents to eyes as FP.
   */
  applyCamera(camera: THREE.PerspectiveCamera): void {
    const target = this._target.set(
      this.x,
      this.y + this.cfg.eyeHeight * 0.85,
      this.z,
    );

    const dist = this.cfg.camDistance;
    const cp = Math.cos(this.pitch);
    const sp = Math.sin(this.pitch);
    const cy = Math.cos(this.yaw);
    const sy = Math.sin(this.yaw);

    // Behind player along yaw, elevated by pitch
    const ox = sy * cp * dist;
    const oy = sp * dist + this.cfg.camHeight * 0.15;
    const oz = cy * cp * dist;

    this._camPos.set(target.x + ox, target.y + oy, target.z + oz);

    // Soft camera collision: pull in if solid between target and camera
    this.pullCameraInto(target, this._camPos);

    camera.position.copy(this._camPos);
    camera.lookAt(target);
  }

  private pullCameraInto(from: THREE.Vector3, to: THREE.Vector3): void {
    this._dir.copy(to).sub(from);
    const len = this._dir.length();
    const minLen = 0.2 * VOXEL_SCALE;
    if (len < minLen) return;
    this._dir.multiplyScalar(1 / len);

    // Sparse samples (every ~4 blocks) — was 1:1 with distance (~84 checks) and tanked FPS.
    const stepSize = Math.max(2, VOXEL_SCALE * 0.2);
    const steps = Math.max(1, Math.ceil(len / stepSize));
    let ok = len;
    for (let i = 1; i <= steps; i++) {
      const t = (i / steps) * len;
      const px = from.x + this._dir.x * t;
      const py = from.y + this._dir.y * t;
      const pz = from.z + this._dir.z * t;
      if (this.world.isSolidWorld(px, py, pz)) {
        ok = Math.max(0.6 * VOXEL_SCALE, t - 0.35 * VOXEL_SCALE);
        break;
      }
    }
    to.copy(from).addScaledVector(this._dir, ok);
  }
}
